import React, { useEffect, useState, Fragment } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, MapPinIcon, CalendarIcon, ClockIcon, CheckIcon, PlaneIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { DateTimePicker } from '../components/DateTimePicker';
import { RideCard } from '../components/RideCard';
import { FlightDetails } from '../components/FlightDetails';
import { PickupRecommendation } from '../components/PickupRecommendation';
import { useApp } from '../context/AppContext';
type ScheduleStep = 'location' | 'datetime' | 'ridetype' | 'confirmation';
export const Schedule: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    savedLocations,
    rideOptions,
    setCurrentRide,
    getCurrency
  } = useApp();
  const passedLocations = location.state as {
    pickup?: Location;
    dropoff?: Location;
  } | null;
  const initialStep: ScheduleStep = passedLocations?.pickup && passedLocations?.dropoff ? 'datetime' : 'location';
  const [step, setStep] = useState<ScheduleStep>(initialStep);
  const [pickup, setPickup] = useState(passedLocations?.pickup?.name || '');
  const [dropoff, setDropoff] = useState(passedLocations?.dropoff?.name || '');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedRideType, setSelectedRideType] = useState('');
  // Airport-specific state
  const [flightType, setFlightType] = useState<'departure' | 'arrival'>('departure');
  const [airline, setAirline] = useState('');
  const [flightNumber, setFlightNumber] = useState('');
  const [airport, setAirport] = useState('');
  const [flightDate, setFlightDate] = useState<Date | null>(null);
  const [flightTime, setFlightTime] = useState<Date | null>(null);
  const [recommendedPickupTime, setRecommendedPickupTime] = useState<Date | null>(null);
  const pickupLocation = passedLocations?.pickup || savedLocations.find(loc => loc.name.toLowerCase().includes(pickup.toLowerCase())) || {
    id: 'custom',
    name: pickup,
    address: pickup,
    country: 'MY' as const
  };
  const dropoffLocation = passedLocations?.dropoff || savedLocations.find(loc => loc.name.toLowerCase().includes(dropoff.toLowerCase())) || {
    id: 'custom',
    name: dropoff,
    address: dropoff,
    country: 'SG' as const
  };
  const currency = getCurrency(pickupLocation, dropoffLocation);
  const selectedRide = rideOptions.find(opt => opt.id === selectedRideType);
  const isAirportRide = selectedRide?.type === 'Airport';
  // Calculate recommended pickup time for airport rides
  useEffect(() => {
    if (isAirportRide && flightDate && flightTime) {
      const flightDateTime = new Date(flightDate);
      flightDateTime.setHours(flightTime.getHours(), flightTime.getMinutes());
      if (flightType === 'departure') {
        // For departures: subtract 2 hours (airport buffer) + 90 min (border) + 40 min (travel)
        const totalMinutes = 120 + 90 + 40;
        const recommended = new Date(flightDateTime.getTime() - totalMinutes * 60000);
        setRecommendedPickupTime(recommended);
        setSelectedDate(recommended);
      } else {
        // For arrivals: add 60 min (landing/baggage) + 45 min (border)
        const totalMinutes = 60 + 45;
        const recommended = new Date(flightDateTime.getTime() + totalMinutes * 60000);
        setRecommendedPickupTime(recommended);
        setSelectedDate(recommended);
      }
    }
  }, [isAirportRide, flightDate, flightTime, flightType]);
  const canProceedFromLocation = pickup && dropoff;
  const canProceedFromDateTime = isAirportRide ? flightDate && flightTime && airline && flightNumber && airport : selectedDate;
  const canProceedFromRideType = selectedRideType;
  const handleNext = () => {
    if (step === 'location' && canProceedFromLocation) {
      setStep('datetime');
    } else if (step === 'datetime' && canProceedFromDateTime) {
      setStep('ridetype');
    } else if (step === 'ridetype' && canProceedFromRideType) {
      setStep('confirmation');
    }
  };
  const handleConfirm = () => {
    if (selectedRide && selectedDate) {
      setCurrentRide({
        pickup: pickupLocation,
        dropoff: dropoffLocation,
        type: selectedRide.type,
        price: selectedRide.price,
        currency,
        date: selectedDate,
        flightDetails: isAirportRide ? {
          type: flightType,
          airline,
          flightNumber,
          airport: airport as any,
          date: flightDate!,
          time: flightTime!
        } : undefined,
        recommendedPickupTime: recommendedPickupTime || undefined
      });
      navigate('/schedule-success');
    }
  };
  const renderStepIndicator = () => {
    const steps = [{
      id: 'location',
      label: 'Location'
    }, {
      id: 'datetime',
      label: 'Date & Time'
    }, {
      id: 'ridetype',
      label: 'Ride Type'
    }, {
      id: 'confirmation',
      label: 'Confirm'
    }];
    const currentIndex = steps.findIndex(s => s.id === step);
    return <div className="flex items-center justify-between mb-8" data-id="element-528">
        {steps.map((s, index) => <Fragment key={s.id} data-id="element-529">
            <div className="flex flex-col items-center" data-id="element-530">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${index <= currentIndex ? 'bg-primary text-background' : 'bg-card border border-primary border-opacity-30 text-white text-opacity-50'}`} data-id="element-531">
                {index < currentIndex ? <CheckIcon size={16} data-id="element-532" /> : <span className="text-sm font-medium" data-id="element-533">{index + 1}</span>}
              </div>
              <span className="text-xs mt-1 text-white text-opacity-70" data-id="element-534">
                {s.label}
              </span>
            </div>
            {index < steps.length - 1 && <div className={`flex-1 h-px mx-2 ${index < currentIndex ? 'bg-primary' : 'bg-primary bg-opacity-30'}`} data-id="element-535" />}
          </Fragment>)}
      </div>;
  };
  return <div className="min-h-screen bg-background" data-id="element-536">
      <div className="p-6" data-id="element-537">
        <div className="flex items-center justify-between mb-6" data-id="element-538">
          <div className="flex items-center" data-id="element-539">
            <button className="p-2 mr-3" onClick={() => step === 'location' ? navigate(-1) : setStep(step === 'datetime' ? 'location' : step === 'ridetype' ? 'datetime' : 'ridetype')} data-id="element-540">
              <ArrowLeftIcon size={24} data-id="element-541" />
            </button>
            <h1 className="text-xl font-semibold" data-id="element-542">Schedule a Ride</h1>
          </div>
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tZW1cXVNSUAs8uLdQVnHaK/jomgo.png" alt="JomGo" className="h-8" data-id="element-543" />
        </div>
        {renderStepIndicator()}
        {step === 'location' && <div data-id="element-544">
            <div className="mb-6" data-id="element-545">
              <h2 className="text-lg font-medium mb-4" data-id="element-546">Where are you going?</h2>
              <div className="space-y-3" data-id="element-547">
                <Input icon="location" placeholder="Pickup location" value={pickup} onChange={e => setPickup(e.target.value)} data-id="element-548" />
                <Input icon="location" placeholder="Dropoff location" value={dropoff} onChange={e => setDropoff(e.target.value)} data-id="element-549" />
              </div>
            </div>
            <div className="mb-6" data-id="element-550">
              <h3 className="text-sm font-medium mb-3 text-white text-opacity-70" data-id="element-551">
                Saved locations
              </h3>
              <div className="space-y-2" data-id="element-552">
                {savedLocations.map(location => <div key={location.id} className="card flex items-center" onClick={() => {
              if (location.country === 'MY') {
                setPickup(location.name);
              } else {
                setDropoff(location.name);
              }
            }} data-id="element-553">
                    <MapPinIcon size={18} className="text-primary mr-3" data-id="element-554" />
                    <div data-id="element-555">
                      <p className="font-medium text-sm" data-id="element-556">{location.name}</p>
                      <p className="text-xs text-white text-opacity-70" data-id="element-557">
                        {location.address}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
            <Button onClick={handleNext} disabled={!canProceedFromLocation} data-id="element-558">
              Continue
            </Button>
          </div>}
        {step === 'datetime' && <div data-id="element-559">
            <div className="mb-6" data-id="element-560">
              <h2 className="text-lg font-medium mb-4" data-id="element-561">
                When do you want to travel?
              </h2>
              {!isAirportRide && <DateTimePicker selectedDate={selectedDate} onDateChange={setSelectedDate} data-id="element-562" />}
            </div>
            {isAirportRide && <div className="space-y-6" data-id="element-563">
                <FlightDetails flightType={flightType} setFlightType={setFlightType} airline={airline} setAirline={setAirline} flightNumber={flightNumber} setFlightNumber={setFlightNumber} airport={airport} setAirport={setAirport} flightDate={flightDate} setFlightDate={setFlightDate} flightTime={flightTime} setFlightTime={setFlightTime} data-id="element-564" />
                {recommendedPickupTime && <PickupRecommendation flightType={flightType} flightTime={flightTime!} recommendedPickupTime={recommendedPickupTime} onAdjust={() => {
            // Allow manual adjustment
            const newTime = prompt('Enter pickup time (HH:MM):', recommendedPickupTime.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }));
            if (newTime) {
              const [hours, minutes] = newTime.split(':').map(Number);
              const adjusted = new Date(recommendedPickupTime);
              adjusted.setHours(hours, minutes);
              setRecommendedPickupTime(adjusted);
              setSelectedDate(adjusted);
            }
          }} data-id="element-565" />}
              </div>}
            {selectedDate && !isAirportRide && <div className="card mb-6" data-id="element-566">
                <div className="flex items-center justify-between" data-id="element-567">
                  <div data-id="element-568">
                    <p className="text-sm text-white text-opacity-70" data-id="element-569">
                      Scheduled for
                    </p>
                    <p className="font-medium" data-id="element-570">
                      {selectedDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                    </p>
                    <p className="text-primary" data-id="element-571">
                      {selectedDate.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit'
                })}
                    </p>
                  </div>
                  <div className="bg-primary bg-opacity-20 p-3 rounded-full" data-id="element-572">
                    <CalendarIcon size={24} className="text-primary" data-id="element-573" />
                  </div>
                </div>
              </div>}
            <Button onClick={handleNext} disabled={!canProceedFromDateTime} data-id="element-574">
              Continue
            </Button>
          </div>}
        {step === 'ridetype' && <div data-id="element-575">
            <div className="mb-6" data-id="element-576">
              <h2 className="text-lg font-medium mb-4" data-id="element-577">
                Choose your ride type
              </h2>
              <div data-id="element-578">
                {rideOptions.map(option => <RideCard key={option.id} type={option.type} price={option.price} currency={currency} duration={option.duration} seats={option.seats} selected={selectedRideType === option.id} onClick={() => setSelectedRideType(option.id)} data-id="element-579" />)}
              </div>
            </div>
            <Button onClick={handleNext} disabled={!canProceedFromRideType} data-id="element-580">
              Continue
            </Button>
          </div>}
        {step === 'confirmation' && selectedRide && selectedDate && <div data-id="element-581">
            <div className="mb-6" data-id="element-582">
              <h2 className="text-lg font-medium mb-4" data-id="element-583">
                Review your scheduled ride
              </h2>
              <div className="card mb-4" data-id="element-584">
                <div className="flex items-center mb-3" data-id="element-585">
                  <CalendarIcon size={18} className="text-primary mr-2" data-id="element-586" />
                  <div data-id="element-587">
                    <p className="text-sm text-white text-opacity-70" data-id="element-588">
                      {isAirportRide ? 'Pickup Time' : 'Travel Date'}
                    </p>
                    <p className="font-medium" data-id="element-589">
                      {selectedDate.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}{' '}
                      at{' '}
                      {selectedDate.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit'
                  })}
                    </p>
                    {isAirportRide && flightTime && <p className="text-primary text-sm mt-1" data-id="element-590">
                        Flight {flightType}: {airline} {flightNumber} •{' '}
                        {flightTime.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit'
                  })}
                      </p>}
                  </div>
                </div>
              </div>
              <div className="card mb-4" data-id="element-591">
                <div className="flex items-start mb-3" data-id="element-592">
                  <MapPinIcon size={18} className="text-primary mt-1 mr-3" data-id="element-593" />
                  <div data-id="element-594">
                    <p className="text-sm text-white text-opacity-70" data-id="element-595">Pickup</p>
                    <p className="font-medium" data-id="element-596">{pickupLocation.name}</p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-597">
                  <MapPinIcon size={18} className="text-primary mt-1 mr-3" data-id="element-598" />
                  <div data-id="element-599">
                    <p className="text-sm text-white text-opacity-70" data-id="element-600">
                      Dropoff
                    </p>
                    <p className="font-medium" data-id="element-601">{dropoffLocation.name}</p>
                  </div>
                </div>
              </div>
              <div className="card mb-4" data-id="element-602">
                <div className="flex items-center justify-between" data-id="element-603">
                  <div data-id="element-604">
                    <p className="text-sm text-white text-opacity-70" data-id="element-605">
                      Ride Type
                    </p>
                    <p className="font-medium" data-id="element-606">{selectedRide.type}</p>
                    <p className="text-xs text-white text-opacity-70 mt-1" data-id="element-607">
                      {selectedRide.duration} • {selectedRide.seats} seats
                    </p>
                  </div>
                  <div className="chip" data-id="element-608">
                    {currency} {selectedRide.price}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-card border border-primary border-opacity-30 rounded-lg p-4 mb-6" data-id="element-609">
              <p className="text-sm text-white text-opacity-70" data-id="element-610">
                <span className="text-primary font-medium" data-id="element-611">Note:</span>{' '}
                {isAirportRide ? 'Your driver will be ready at the recommended pickup time. Track real-time traffic updates in your booking.' : "Your ride will be confirmed closer to your scheduled time. You'll receive a notification when a driver is assigned."}
              </p>
            </div>
            <Button onClick={handleConfirm} data-id="element-612">Confirm Schedule</Button>
          </div>}
      </div>
    </div>;
};